import { JSX, useEffect, useState } from "react";
import PageHeader from "../../components/PageHeader";
import Tabs from "../../components/Tabs";
import { EditModeProvider, useEditMode } from "../../context/EditModeContext";
import CustomButton from "../../components/CustomButton";
import SchoolDetailsForm from "./SchoolDetails";
import AcademicPermissions from "./AcademicPermissions";
import AcademiCycles from "./AcademicCycles";
import SchoolTiming from "./SchoolTimings";
import OtherDetails from "./OtherDetails";
import useUrlTab from "../../util/hooks/useUrlTab";
import { AppDispatch, RootState } from "../../store";
import { useDispatch } from "react-redux";
import { getSchoolBranchByCode } from "../../features/school-branch-controller/schoolBranchSlice";
import { useSelector } from "react-redux";
import Subjects from "./Subjects";
import {
  SchoolBranch,
  UpdateSchoolBranchPayload,
} from "../../types/schoolBranchType";
import SchoolBranchApis from "../../api/schoolBranchApis";
import { toast } from "sonner";

const tabOptions = [
  "School Details",
  "Permissions",
  "Academic Cycles",
  "Subjects",
  "Others",
];

function AcademicPreferenceContent() {
  const { activeTab, handleTabChange } = useUrlTab(tabOptions);
  const { isEditing, toggleEdit } = useEditMode();
  const dispatch = useDispatch<AppDispatch>();
  const { branchDetails, isLoading } = useSelector(
    (state: RootState) => state.schoolBranch,
  );
  const [updatedBranchData, setUpdatedBranchData] =
    useState<UpdateSchoolBranchPayload>({});

  const branchCode = useSelector(
    (state: RootState) => state.access.schoolBranch?.schoolBranchCode,
  );

  useEffect(() => {
    dispatch(getSchoolBranchByCode());
  }, [dispatch, branchCode]);

  const handleUpdateBranch = async () => {
    const payload: UpdateSchoolBranchPayload = {
      ...branchDetails,
      ...updatedBranchData,
    };

    payload.schoolCode = branchDetails.schoolCode;

    delete (payload as any).schoolBranchIcon;

    try {
      await SchoolBranchApis.updateBranch(
        branchDetails.schoolBranchId,
        payload,
      );

      toast.success("Branch updated");
      setUpdatedBranchData({});
      toggleEdit();
    } catch (err) {
      toast.error("Failed to update");
      console.error("Failed to update branch:", err);
    }
  };

  const tabContent: Record<string, JSX.Element> = {
    "School Details": (
      <>
        <SchoolDetailsForm
          branch={branchDetails}
          isLoading={isLoading}
          onDataChange={(data) =>
            setUpdatedBranchData((prev) => ({ ...prev, ...data }))
          }
        />

        <SchoolTiming
          branch={branchDetails}
          isLoading={isLoading}
          onDataChange={(data) =>
            setUpdatedBranchData((prev) => ({ ...prev, ...data }))
          }
        />
      </>
    ),
    Permissions: <AcademicPermissions />,
    "Academic Cycles": (
      <>
        <AcademiCycles />
      </>
    ),
    Subjects: (
      <>
        <Subjects />
      </>
    ),
    Others: (
      <>
        <OtherDetails />
      </>
    ),
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="">
        <PageHeader showBack />
      </div>
      <div className="bg-white rounded-lg w-full grid py-5 divide-y divide-neutral-50">
        <div className="w-full flex flex-col lg:flex-row justify-between gap-4 lg:items-center overflow-x-auto lg:overflow-visible">
          <div className="lg:flex-1 flex justify-center items-center">
            <Tabs
              tabs={tabOptions}
              onTabChange={handleTabChange}
              activeTab={activeTab}
            />
          </div>
          <div className="flex gap-3 justify-end flex-col items-end px-7">
            {(activeTab === "School Details" || activeTab === "Others") && (
              <>
                {isEditing ? (
                  <>
                    {activeTab === "School Details" && (
                      <CustomButton
                        text="Submit"
                        color="bg-primary-500"
                        onClick={handleUpdateBranch}
                        disabled={!Object.keys(updatedBranchData).length}
                      />
                    )}

                    <CustomButton
                      text="Cancel"
                      color="bg-error-500"
                      onClick={() => {
                        setUpdatedBranchData({});
                        toggleEdit();
                      }}
                    />
                  </>
                ) : (
                  <CustomButton
                    text="Edit"
                    color="bg-warning-600"
                    onClick={toggleEdit}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {tabContent[activeTab]}
      </div>
    </div>
  );
}

export default function AcademicPreference() {
  return (
    <EditModeProvider>
      <AcademicPreferenceContent />
    </EditModeProvider>
  );
}
